import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "apiKey", "url", "js", "jsTimeout",
    "waitFor", "proxy", "country", "question", "preview", "response",
    "responseArea", "language", "codePreview", "tryButton", "spinner",
    "format", "rawResponse", "htmlResponse", "responseFrame",
    "headers", "timeout", "jsScript", "returnScriptResult", "customProxy",
    "credits", "device", "errorOn404", "errorOnRedirect",
    "responseFormat", "endpoint", "aiOptions", "fields", "aiFields",
    "textOptions", "textFormat", "returnLinks", "devApi", "turnstile",
    "copyCodeButton", "cancelButton"
  ]

  static values = {
    currentUserApiKey: String,
    headerRowTemplate: String,
    fieldRowTemplate: String
  }

  connect() {
    // Initialize abort controller for request cancellation
    this.abortController = null
    
    // Create default templates if not already set
    if (!this.headerRowTemplateValue) {
      this.headerRowTemplateValue = `
        <div class="header-row flex gap-2">
          <input type="text" placeholder="Key" class="flex-1 rounded-md border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" data-action="input->api-request-builder#updateRequest">
          <input type="text" placeholder="Value" class="flex-1 rounded-md border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" data-action="input->api-request-builder#updateRequest">
          <button type="button" class="text-red-600 hover:text-red-800" data-action="click->api-request-builder#removeHeader">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      `.trim()
    }
    
    if (!this.fieldRowTemplateValue) {
      this.fieldRowTemplateValue = `
        <div class="field-row flex gap-2">
          <input type="text" placeholder="product_description" class="w-1/3 rounded-md border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" data-action="input->api-request-builder#updateRequest">
          <textarea placeholder="Description of the product on the page" rows="2" class="flex-1 rounded-md border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-y" data-action="input->api-request-builder#updateRequest"></textarea>
          <button type="button" class="text-red-600 hover:text-red-800 self-start mt-2" data-action="click->api-request-builder#removeField">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      `.trim()
    }
    
    // Try to save templates from existing DOM elements as backup
    const firstRow = this.headersTarget.querySelector('.header-row')
    if (firstRow) {
      this.headerRowTemplateValue = firstRow.outerHTML
    }

    const firstFieldRow = this.fieldsTarget.querySelector('.field-row')
    if (firstFieldRow) {
      this.fieldRowTemplateValue = firstFieldRow.outerHTML
    }

    // Check if URL has shared parameters
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('share')) {
      this.loadSharedState(urlParams)
    } else {
      this.loadStateFromStorage()
    }
    
    this.updateRequest()
    this.toggleAiOptions()
    this.toggleTextOptions()
    this.toggleAiFields()
    this.initTooltips()

    // Initialize turnstile if target exists
    if (this.hasTurnstileTarget) {
      this.initTurnstile()
      this.updateTryButtonState()
    }
  }

  initTurnstile() {
    // Load the script if not already loaded
    if (!window.turnstile) {
      const script = document.createElement('script')
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
      script.async = true
      script.defer = true
      
      script.onload = () => {
        this.renderTurnstile()
      }
      
      document.head.appendChild(script)
    } else {
      this.renderTurnstile()
    }
  }

  renderTurnstile() {
    window.turnstile.render(this.turnstileTarget, {
      theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
      callback: () => {
        this.updateRequest()
        this.updateTryButtonState()
      }
    })
  }

  updateTryButtonState() {
    this.tryButtonTarget.disabled = this.hasTurnstileTarget && !window.turnstile?.getResponse(this.turnstileTarget)
  }

  loadSharedState(params) {
    // Load values from URL parameters
    const fields = {
      apiKey: { target: 'apiKey', default: this.currentUserApiKeyValue || '' },
      url: { target: 'url' },
      js: { target: 'js', type: 'checkbox' },
      jsTimeout: { target: 'jsTimeout' },
      waitFor: { target: 'waitFor' },
      country: { target: 'country' },
      question: { target: 'question' },
      timeout: { target: 'timeout' },
      jsScript: { target: 'jsScript' },
      returnScriptResult: { target: 'returnScriptResult', type: 'checkbox' },
      customProxy: { target: 'customProxy' },
      device: { target: 'device' },
      errorOn404: { target: 'errorOn404', type: 'checkbox' },
      errorOnRedirect: { target: 'errorOnRedirect', type: 'checkbox' },
      textFormat: { target: 'textFormat' },
      returnLinks: { target: 'returnLinks', type: 'checkbox' }
    }

    // Only add devApi if the target exists (admin only)
    if (this.hasDevApiTarget) {
      fields.devApi = { target: 'devApi', type: 'checkbox' }
    }

    for (const [key, config] of Object.entries(fields)) {
      const value = params.get(`form[${key}]`)
      if (value !== null) {
        const target = this[`${config.target}Target`]
        if (config.type === 'checkbox') {
          target.checked = value === 'true'
        } else {
          target.value = value
        }
      }
    }

    // Handle radio groups
    const radioGroups = {
      proxy: { targets: 'proxyTargets' },
      endpoint: { targets: 'endpointTargets' },
      format: { targets: 'formatTargets' }
    }

    for (const [key, config] of Object.entries(radioGroups)) {
      const value = params.get(`form[${key}]`)
      if (value) {
        const radio = this[config.targets].find(el => el.value === value)
        if (radio) {
          radio.checked = true
        }
      }
    }

    // Handle headers
    const headers = params.get('form[headers]')
    if (headers) {
      try {
        const parsedHeaders = JSON.parse(headers)
        if (Array.isArray(parsedHeaders)) {
          this.headersTarget.innerHTML = ''
          parsedHeaders.forEach(header => {
            if (header && typeof header === 'object' && header.key && header.value) {
              this.addHeaderRow(header.key, header.value)
            }
          })
        }
      } catch (e) {
        console.error('Failed to parse shared headers:', e)
      }
    }

    // Handle fields
    const aiFields = params.get('form[fields]')
    if (aiFields) {
      try {
        const parsedFields = JSON.parse(aiFields)
        if (Array.isArray(parsedFields)) {
          this.fieldsTarget.innerHTML = ''
          parsedFields.forEach(field => {
            if (field && typeof field === 'object' && field.key && field.value) {
              this.addFieldRow(field.key, field.value)
            }
          })
        }
      } catch (e) {
        console.error('Failed to parse shared fields:', e)
      }
    }

    // Handle language
    const language = params.get('form[language]')
    if (language) {
      const langRadio = this.languageTargets.find(el => el.value === language)
      if (langRadio) langRadio.checked = true
    }
  }

  shareForm() {
    const params = new URLSearchParams()

    // Add share parameter
    params.set('share', 'true')
    
    // Add all form values
    const formValues = {
      url: this.urlTarget.value,
      js: this.jsTarget.checked,
      jsTimeout: this.jsTimeoutTarget.value,
      waitFor: this.waitForTarget.value,
      country: this.countryTarget.value,
      question: this.questionTarget.value,
      timeout: this.timeoutTarget.value,
      jsScript: this.jsScriptTarget.value,
      returnScriptResult: this.returnScriptResultTarget.checked,
      customProxy: this.customProxyTarget.value,
      device: this.deviceTarget.value,
      errorOn404: this.errorOn404Target.checked,
      errorOnRedirect: this.errorOnRedirectTarget.checked,
      textFormat: this.textFormatTarget.value,
      returnLinks: this.returnLinksTarget.checked
    }

    // Only add devApi if the target exists (admin only)
    if (this.hasDevApiTarget) {
      formValues.devApi = this.devApiTarget.checked
    }

    for (const [key, value] of Object.entries(formValues)) {
      if (value !== '' && value !== false) {
        params.set(`form[${key}]`, value)
      }
    }

    // Add radio selections
    const radioSelections = {
      proxy: this.proxyTargets,
      endpoint: this.endpointTargets,
      format: this.formatTargets
    }

    for (const [key, targets] of Object.entries(radioSelections)) {
      const selected = targets.find(el => el.checked)
      if (selected) {
        params.set(`form[${key}]`, selected.value)
      }
    }

    // Add headers
    const headers = []
    this.headersTarget.querySelectorAll('.header-row').forEach(row => {
      const inputs = row.querySelectorAll('input')
      if (inputs[0].value && inputs[1].value) {
        headers.push({
          key: inputs[0].value,
          value: inputs[1].value
        })
      }
    })
    if (headers.length > 0) {
      params.set('form[headers]', JSON.stringify(headers))
    }

    // Add fields
    const fields = []
    this.fieldsTarget.querySelectorAll('.field-row').forEach(row => {
      const keyInput = row.querySelector('input')
      const valueTextarea = row.querySelector('textarea')
      if (keyInput.value && valueTextarea.value) {
        fields.push({
          key: keyInput.value,
          value: valueTextarea.value
        })
      }
    })
    if (fields.length > 0) {
      params.set('form[fields]', JSON.stringify(fields))
    }

    // Add selected language
    const selectedLanguage = this.languageTargets.find(el => el.checked)
    if (selectedLanguage) {
      params.set('form[language]', selectedLanguage.value)
    }

    // Create shareable URL
    const shareableUrl = `${window.location.origin}${window.location.pathname}?${params.toString()}`
    
    // Copy to clipboard
    const button = event.currentTarget
    const originalText = button.innerHTML
    this.copyToClipboard(shareableUrl)
      .then(() => {
        button.innerHTML = 'Copied!'
        setTimeout(() => {
          button.innerHTML = originalText
        }, 2000)
      })
      .catch(err => {
        console.error('Failed to copy URL:', err)
        // Show error feedback to user
        button.innerHTML = 'Copy failed'
        setTimeout(() => {
          button.innerHTML = originalText
        }, 2000)
      })
  }

  loadStateFromStorage() {
    // Define the fields to load from localStorage with their default values
    const fields = {
      apiKey: { target: 'apiKey', default: this.currentUserApiKeyValue || 'test-api-key' },
      url: { target: 'url', default: 'https://example.com' },
      js: { target: 'js', default: true, type: 'checkbox' },
      jsTimeout: { target: 'jsTimeout', default: '2000' },
      waitFor: { target: 'waitFor', default: '' },
      country: { target: 'country', default: 'us' },
      question: { target: 'question', default: '' },
      timeout: { target: 'timeout', default: '10000' },
      jsScript: { target: 'jsScript', default: '' },
      returnScriptResult: { target: 'returnScriptResult', default: false, type: 'checkbox' },
      customProxy: { target: 'customProxy', default: '' },
      device: { target: 'device', default: 'desktop' },
      errorOn404: { target: 'errorOn404', default: false, type: 'checkbox' },
      errorOnRedirect: { target: 'errorOnRedirect', default: false, type: 'checkbox' },
      format: { target: 'format', default: 'text' },
      textFormat: { target: 'textFormat', default: 'plain' },
      returnLinks: { target: 'returnLinks', default: false, type: 'checkbox' }
    }

    // Only add devApi if the target exists (admin only)
    if (this.hasDevApiTarget) {
      fields.devApi = { target: 'devApi', default: false, type: 'checkbox' }
    }

    // Load values for each field
    for (const [key, config] of Object.entries(fields)) {
      const storedValue = localStorage.getItem(key)
      const target = this[`${config.target}Target`]

      if (config.type === 'checkbox') {
        target.checked = storedValue === null ? config.default : storedValue === 'true'
      } else {
        target.value = storedValue || config.default
      }
    }

    // Handle radio button groups
    const radioGroups = {
      proxy: { targets: 'proxyTargets', default: 'datacenter' },
      endpoint: { targets: 'endpointTargets', default: 'html' },
      format: { targets: 'formatTargets', default: 'text' }
    }

    for (const [key, config] of Object.entries(radioGroups)) {
      const savedValue = localStorage.getItem(key) || config.default
      const radio = this[config.targets].find(el => el.value === savedValue)
      if (radio) {
        radio.checked = true
      } else {
        // Fallback to default if saved value is invalid
        const defaultRadio = this[config.targets].find(el => el.value === config.default)
        if (defaultRadio) defaultRadio.checked = true
      }
    }

    // Handle headers
    let savedHeaders = []
    try {
      savedHeaders = JSON.parse(localStorage.getItem('headers') || '[]')
    } catch (e) {
      console.warn('Failed to parse saved headers:', e)
      savedHeaders = []
    }
    this.headersTarget.innerHTML = ''
    
    if (savedHeaders.length > 0) {
      savedHeaders.forEach(header => this.addHeaderRow(header.key, header.value))
    } else {
      this.addHeaderRow('', '')
    }

    // Handle fields
    let savedFields = []
    try {
      savedFields = JSON.parse(localStorage.getItem('fields') || '[]')
    } catch (e) {
      console.warn('Failed to parse saved fields:', e)
      savedFields = []
    }
    this.fieldsTarget.innerHTML = ''
    
    if (savedFields.length > 0) {
      savedFields.forEach(field => this.addFieldRow(field.key, field.value))
    } else {
      this.addFieldRow('', '')
    }

    // Handle language selection
    const savedLanguage = localStorage.getItem('language')
    if (savedLanguage) {
      this.languageTargets.find(el => el.value === savedLanguage).checked = true
    }
  }

  saveStateToStorage() {
    // Don't save if share=true is in URL
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('share')) {
      return
    }

    // Save direct values
    const directValues = {
      'apiKey': this.apiKeyTarget.value,
      'url': this.urlTarget.value, 
      'js': this.jsTarget.checked,
      'jsTimeout': this.jsTimeoutTarget.value,
      'waitFor': this.waitForTarget.value,
      'country': this.countryTarget.value,
      'question': this.questionTarget.value,
      'timeout': this.timeoutTarget.value,
      'jsScript': this.jsScriptTarget.value,
      'returnScriptResult': this.returnScriptResultTarget.checked,
      'customProxy': this.customProxyTarget.value,
      'device': this.deviceTarget.value,
      'errorOn404': this.errorOn404Target.checked,
      'errorOnRedirect': this.errorOnRedirectTarget.checked,
      'textFormat': this.textFormatTarget.value,
      'returnLinks': this.returnLinksTarget.checked
    }

    // Only add devApi if the target exists (admin only)
    if (this.hasDevApiTarget) {
      directValues.devApi = this.devApiTarget.checked
    }

    for (const [key, value] of Object.entries(directValues)) {
      localStorage.setItem(key, value)
    }

    // Save selected radio values
    const radioSelections = {
      'proxy': this.proxyTargets,
      'endpoint': this.endpointTargets,
      'format': this.formatTargets
    }

    for (const [key, targets] of Object.entries(radioSelections)) {
      const selected = targets.find(el => el.checked)
      if (selected) {
        localStorage.setItem(key, selected.value)
      }
    }

    // Save headers
    const headers = []
    this.headersTarget.querySelectorAll('.header-row').forEach(row => {
      const inputs = row.querySelectorAll('input')
      if (inputs[0].value && inputs[1].value) {
        headers.push({
          key: inputs[0].value,
          value: inputs[1].value
        })
      }
    })
    localStorage.setItem('headers', JSON.stringify(headers))

    // Save fields
    const fields = []
    this.fieldsTarget.querySelectorAll('.field-row').forEach(row => {
      const keyInput = row.querySelector('input')
      const valueTextarea = row.querySelector('textarea')
      if (keyInput.value && valueTextarea.value) {
        fields.push({
          key: keyInput.value,
          value: valueTextarea.value
        })
      }
    })
    localStorage.setItem('fields', JSON.stringify(fields))

    // Save selected language
    const selectedLanguage = this.languageTargets.find(el => el.checked)
    if (selectedLanguage) {
      localStorage.setItem('language', selectedLanguage.value)
    }
  }

  resetForm() {
    const keysToRemove = [
      'apiKey', 'url', 'js', 'jsTimeout', 'waitFor', 'proxy', 'country', 
      'question', 'timeout', 'jsScript', 'returnScriptResult', 'customProxy',
      'headers', 'fields', 'language', 'device', 'errorOn404',
      'errorOnRedirect', 'format', 'endpoint', 'textFormat', 'returnLinks'
    ]
    
    // Only add devApi if the target exists (admin only)
    if (this.hasDevApiTarget) {
      keysToRemove.push('devApi')
    }
    
    for (const key of keysToRemove) {
      localStorage.removeItem(key)
    }
    
    this.loadStateFromStorage()
    
    // Reset headers
    this.headersTarget.innerHTML = ''
    this.addHeaderRow('', '')

    // Reset fields
    this.fieldsTarget.innerHTML = ''
    this.addFieldRow('', '')
    
    // Update UI visibility based on reset state
    this.toggleAiFields()
    this.toggleAiOptions()
    this.toggleTextOptions()
    
    this.updateRequest()
  }

  updateRequest() {
    const selectedEndpoint = this.endpointTargets.find(el => el.checked)
    let endpoint = selectedEndpoint ? selectedEndpoint.value : 'html'
    let params = this.buildParams()
    const queryString = new URLSearchParams(params).toString()
    
    // Use dev API domain if devApi is checked and the target exists
    const apiDomain = this.hasDevApiTarget && this.devApiTarget.checked ? 
      'https://api-dev.webscraping.ai' : 
      'https://api.webscraping.ai'
    
    this.resultUrl = `${apiDomain}/${endpoint}?${queryString}`
    
    this.updateCodePreview(endpoint, params)
    this.updateCredits()
    this.saveStateToStorage()
    this.updateTryButtonState()
  }

  updateCredits() {
    let credits = 5 // Default: js=true + proxy=datacenter
    
    const selectedEndpoint = this.endpointTargets.find(el => el.checked)
    const isJs = this.jsTarget.checked
    const isResidential = this.proxyTargets.find(el => el.checked).value === 'residential'

    if (!isJs && !isResidential) credits = 1
    else if (!isJs && isResidential) credits = 10
    else if (isJs && !isResidential) credits = 5
    else if (isJs && isResidential) credits = 25

    if (selectedEndpoint && selectedEndpoint.value.startsWith('ai/')) {
      credits += 5
    }

    this.creditsTarget.textContent = credits
  }

  toggleAiOptions() {
    const selectedEndpoint = this.endpointTargets.find(el => el.checked)
    const isAiEndpoint = selectedEndpoint && selectedEndpoint.value === 'ai/question'
    this.aiOptionsTargets.forEach(target => {
      target.classList.toggle('hidden', !isAiEndpoint)
    })
    this.updateRequest()
  }

  toggleTextOptions() {
    const selectedEndpoint = this.endpointTargets.find(el => el.checked)
    const isTextEndpoint = selectedEndpoint && selectedEndpoint.value === 'text'
    this.textOptionsTargets.forEach(target => {
      target.classList.toggle('hidden', !isTextEndpoint)
    })
    this.updateRequest()
  }

  toggleAiFields() {
    const selectedEndpoint = this.endpointTargets.find(el => el.checked)
    const isAiEndpoint = selectedEndpoint && selectedEndpoint.value === 'ai/fields'
    this.aiFieldsTargets.forEach(target => {
      target.classList.toggle('hidden', !isAiEndpoint)
    })
    this.updateRequest()
  }

  toggleJsAccordion(event) {
    const button = event.currentTarget
    const icon = button.querySelector('[data-js-accordion-icon]')
    const content = button.nextElementSibling
    
    // Toggle content visibility
    content.classList.toggle('hidden')
    
    // Rotate icon
    if (content.classList.contains('hidden')) {
      icon.style.transform = 'rotate(0deg)'
    } else {
      icon.style.transform = 'rotate(180deg)'
    }
  }

  buildParams() {
    const params = {
      api_key: this.apiKeyTarget.value,
      url: this.urlTarget.value,
      timeout: this.timeoutTarget.value
    }

    const metadata = {}

    // Add turnstile token if available
    if (this.hasTurnstileTarget && window.turnstile) {
      const token = window.turnstile.getResponse(this.turnstileTarget)
      if (token) {
        metadata.turnstile_token = token
      }
    }

    // Only add metadata if it has any values
    if (Object.keys(metadata).length > 0) {
      params.metadata = JSON.stringify(metadata)
    }

    // Build headers from input fields
    const headers = {}
    const headerRows = this.headersTarget.querySelectorAll('.header-row')
    headerRows.forEach(row => {
      const key = row.querySelector('input[placeholder="Key"]').value
      const value = row.querySelector('input[placeholder="Value"]').value
      if (key && value) {
        headers[key] = value
      }
    })
    if (Object.keys(headers).length > 0) {
      params.headers = JSON.stringify(headers)
    }

    if (this.jsTarget.checked) {
      params.js_timeout = this.jsTimeoutTarget.value

      if (this.waitForTarget.value) {
        params.wait_for = this.waitForTarget.value
      }

      if (this.jsScriptTarget.value) {
        params.js_script = this.jsScriptTarget.value
      }

      if (this.returnScriptResultTarget.checked) {
        params.return_script_result = "true"
      }
    } else {
      // API defaults to js=true, so we need to explicitly set js=false when unchecked
      params.js = "false"
    }

    if (this.customProxyTarget.value) {
      params.customProxy = this.customProxyTarget.value
    } else {
      const selectedProxy = this.proxyTargets.find(el => el.checked)
      if (selectedProxy && selectedProxy.value !== "datacenter") {
        params.proxy = selectedProxy.value
      }
    }

    if (this.countryTarget.value !== "us") {
      params.country = this.countryTarget.value
    }

    const selectedEndpoint = this.endpointTargets.find(el => el.checked)
    if (selectedEndpoint && selectedEndpoint.value === 'ai/question') {
      params.question = this.questionTarget.value
    }

    if (selectedEndpoint && selectedEndpoint.value === 'ai/fields') {
      const fields = {}
      const fieldRows = this.fieldsTarget.querySelectorAll('.field-row')
      fieldRows.forEach(row => {
        const key = row.querySelector('input[placeholder="product_description"]').value
        const value = row.querySelector('textarea[placeholder="Description of the product on the page"]').value
        if (key && value) {
          fields[key] = value
        }
      })
      if (Object.keys(fields).length > 0) {
        params.fields = JSON.stringify(fields)
      }
    }

    if (selectedEndpoint && selectedEndpoint.value === 'text') {
      if (this.textFormatTarget.value !== "plain") {
        params.text_format = this.textFormatTarget.value
      }
      if (this.returnLinksTarget.checked) {
        params.return_links = "true"
      }
    }

    if (this.deviceTarget.value !== "desktop") {
      params.device = this.deviceTarget.value
    }

    if (this.errorOn404Target.checked) {
      params.error_on_404 = "true"
    }

    if (this.errorOnRedirectTarget.checked) {
      params.error_on_redirect = "true"
    }

    const selectedFormat = this.formatTargets.find(el => el.checked)
    if (selectedFormat && selectedFormat.value !== "text") {
      params.format = selectedFormat.value
    }

    return params
  }

  addHeaderRow(key = '', value = '') {
    const template = document.createElement('div')
    template.innerHTML = this.headerRowTemplateValue
    const newRow = template.firstElementChild
    
    const inputs = newRow.querySelectorAll('input')
    inputs[0].value = key
    inputs[1].value = value
    
    this.headersTarget.appendChild(newRow)
  }

  addHeader() {
    this.addHeaderRow()
    this.updateRequest()
  }

  removeHeader(event) {
    const headerRows = this.headersTarget.querySelectorAll('.header-row')
    if (headerRows.length > 1) {
      event.target.closest('.header-row').remove()
      this.updateRequest()
    } else {
      // Clear inputs if it's the last row
      event.target.closest('.header-row').querySelectorAll('input').forEach(input => input.value = '')
      this.updateRequest()
    }
  }

  addFieldRow(key = '', value = '') {
    const template = document.createElement('div')
    template.innerHTML = this.fieldRowTemplateValue
    const newRow = template.firstElementChild
    
    const keyInput = newRow.querySelector('input')
    const valueTextarea = newRow.querySelector('textarea')
    keyInput.value = key
    valueTextarea.value = value
    
    this.fieldsTarget.appendChild(newRow)
  }

  addField() {
    this.addFieldRow()
    this.updateRequest()
  }

  removeField(event) {
    const fieldRows = this.fieldsTarget.querySelectorAll('.field-row')
    if (fieldRows.length > 1) {
      event.target.closest('.field-row').remove()
      this.updateRequest()
    } else {
      // Clear inputs if it's the last row
      const row = event.target.closest('.field-row')
      row.querySelector('input').value = ''
      row.querySelector('textarea').value = ''
      this.updateRequest()
    }
  }

  updateCodePreview(endpoint, params) {
    const selectedLanguage = this.languageTargets.find(el => el.checked).value
    // Use dev API domain if devApi is checked and the target exists
    const apiDomain = this.hasDevApiTarget && this.devApiTarget.checked ? 
      'https://api-dev.webscraping.ai' : 
      'https://api.webscraping.ai'
    const baseUrl = apiDomain + '/' + endpoint

    // Create a clean version of params without metadata for code generation
    const cleanParams = { ...params }
    delete cleanParams.metadata
    
    let code = ''

    switch(selectedLanguage) {
      case 'curl':
        // Build curl command with soft wraps for readability
        const queryString = new URLSearchParams(cleanParams).toString()
        if (queryString.length > 50) {
          // Split query parameters for readability
          const paramEntries = Object.entries(cleanParams)
          code = `curl "${baseUrl}?${paramEntries[0][0]}=${encodeURIComponent(paramEntries[0][1])}" \\\n`
          
          for (let i = 1; i < paramEntries.length; i++) {
            const [key, value] = paramEntries[i]
            const prefix = i === paramEntries.length - 1 ? '     ' : '     '
            code += `${prefix}"&${key}=${encodeURIComponent(value)}"${i < paramEntries.length - 1 ? ' \\' : ''}\n`
          }
          code = code.trimEnd()
        } else {
          code = `curl "${baseUrl}?${queryString}"`
        }
        break
      case 'nodejs':
        code = `const https = require('https');

const params = ${JSON.stringify(cleanParams, null, 2)};
const queryString = new URLSearchParams(params).toString();
const url = '${baseUrl}?' + queryString;

https.get(url, (resp) => {
  let data = '';
  resp.on('data', (chunk) => { data += chunk; });
  resp.on('end', () => {
    console.log(data);
  });
}).on("error", (err) => {
  console.log("Error: " + err.message);
});`
        break
      case 'python':
        code = `import requests

params = ${JSON.stringify(cleanParams, null, 2)}
response = requests.get('${baseUrl}', params=params)
print(response.text)`
        break
      case 'ruby':
        code = `require 'net/http'
require 'uri'

params = ${JSON.stringify(cleanParams, null, 2)}
uri = URI('${baseUrl}')
uri.query = URI.encode_www_form(params)

response = Net::HTTP.get_response(uri)
puts response.body`
        break
      case 'php':
        code = `<?php
$params = ${JSON.stringify(cleanParams, null, 2)};
$query = http_build_query($params);
$url = '${baseUrl}?' . $query;
$response = file_get_contents($url);
echo $response;
?>`
        break
    }

    // Update code preview with language class
    this.codePreviewTarget.textContent = code
    
    // Map language names to Prism's language identifiers
    const languageMap = {
      'nodejs': 'javascript',
      'python': 'python',
      'ruby': 'ruby', 
      'php': 'php',
      'curl': 'bash'
    }
    const prismLanguage = languageMap[selectedLanguage] || selectedLanguage
    this.codePreviewTarget.className = `language-${prismLanguage}`
    
    // Trigger syntax highlighting if Prism is available
    if (typeof Prism !== 'undefined') {
      Prism.highlightElement(this.codePreviewTarget)
    }
  }

  async executeRequest() {
    try {
      // Cancel any existing request
      if (this.abortController) {
        this.abortController.abort()
      }
      
      // Create new abort controller for this request
      this.abortController = new AbortController()
      
      // Hide response area and clear previous response
      this.responseAreaTarget.style.display = "none"
      this.responseTarget.textContent = ""
      this.responseFrameTarget.srcdoc = ""
      
      // Show spinner, disable try button, show cancel button
      this.spinnerTarget.classList.remove("hidden")
      this.tryButtonTarget.disabled = true
      this.cancelButtonTarget.classList.remove("hidden")

      const response = await fetch(this.resultUrl, {
        signal: this.abortController.signal
      })
      const data = await response.text()
      
      // Try to parse and format JSON responses
      let formattedData = data
      try {
        const jsonData = JSON.parse(data)
        formattedData = JSON.stringify(jsonData, null, 2)
        
        // Add syntax highlighting for JSON
        this.responseTarget.innerHTML = this.highlightJson(formattedData)
      } catch (e) {
        // Not JSON, display as plain text
        this.responseTarget.textContent = data
      }
      
      this.responseAreaTarget.style.display = "block"

      // Handle HTML preview if selected
      const selectedFormatElement = this.responseFormatTargets.find(el => el.checked)
      const selectedFormat = selectedFormatElement ? selectedFormatElement.value : 'raw'
      if (selectedFormat === 'html') {
        this.rawResponseTarget.classList.add('hidden')
        this.htmlResponseTarget.classList.remove('hidden')
        this.responseFrameTarget.srcdoc = data
      } else {
        this.rawResponseTarget.classList.remove('hidden')
        this.htmlResponseTarget.classList.add('hidden')
      }

      // Refresh turnstile if present
      if (this.hasTurnstileTarget && window.turnstile) {
        window.turnstile.reset(this.turnstileTarget)
        this.updateTryButtonState()
      }

    } catch (error) {
      // Don't show error for aborted requests
      if (error.name === 'AbortError') {
        console.log('Request was aborted')
        return
      }
      
      this.responseTarget.textContent = `Error: ${error.message}`
      this.responseAreaTarget.style.display = "block"
      this.rawResponseTarget.classList.remove('hidden')
      this.htmlResponseTarget.classList.add('hidden')
    } finally {
      // Hide spinner, enable try button, hide cancel button
      this.spinnerTarget.classList.add("hidden") 
      this.tryButtonTarget.disabled = false
      this.cancelButtonTarget.classList.add("hidden")
      
      // Clear abort controller
      this.abortController = null
    }
  }

  cancelRequest() {
    if (this.abortController) {
      this.abortController.abort()
      // Don't show any output - just clean up the UI
      // The finally block in executeRequest will handle UI cleanup
    }
  }

  toggleResponseFormat() {
    const selectedFormatElement = this.responseFormatTargets.find(el => el.checked)
    const selectedFormat = selectedFormatElement ? selectedFormatElement.value : 'raw'
    if (selectedFormat === 'html') {
      this.rawResponseTarget.classList.add('hidden')
      this.htmlResponseTarget.classList.remove('hidden')
      if (this.responseTarget.textContent) {
        this.responseFrameTarget.srcdoc = this.responseTarget.textContent
      }
    } else {
      this.rawResponseTarget.classList.remove('hidden')
      this.htmlResponseTarget.classList.add('hidden')
    }
    this.saveStateToStorage()
  }


  copyGeneratedCode() {
    const code = this.codePreviewTarget.textContent
    const button = this.copyCodeButtonTarget
    const originalText = button.textContent
    
    this.copyToClipboard(code).then(() => {
      button.textContent = 'Copied!'
      button.classList.remove('bg-gray-700', 'hover:bg-gray-600')
      button.classList.add('bg-green-600', 'hover:bg-green-700')
      
      setTimeout(() => {
        button.textContent = originalText
        button.classList.remove('bg-green-600', 'hover:bg-green-700')
        button.classList.add('bg-gray-700', 'hover:bg-gray-600')
      }, 2000)
    }).catch(err => {
      console.error('Failed to copy code:', err)
      button.textContent = 'Copy failed'
      button.classList.remove('bg-gray-700', 'hover:bg-gray-600')
      button.classList.add('bg-red-600', 'hover:bg-red-700')
      
      setTimeout(() => {
        button.textContent = originalText
        button.classList.remove('bg-red-600', 'hover:bg-red-700')
        button.classList.add('bg-gray-700', 'hover:bg-gray-600')
      }, 2000)
    })
  }

  // Clipboard helper with fallback
  async copyToClipboard(text) {
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text)
        return
      } catch (err) {
        console.warn('Clipboard API failed, trying fallback:', err)
      }
    }
    
    // Fallback method
    return new Promise((resolve, reject) => {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.left = '-999999px'
      textarea.style.top = '-999999px'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      
      try {
        const successful = document.execCommand('copy')
        document.body.removeChild(textarea)
        if (successful) {
          resolve()
        } else {
          reject(new Error('document.execCommand failed'))
        }
      } catch (err) {
        document.body.removeChild(textarea)
        reject(err)
      }
    })
  }

  // Add helper method to check if devApi target exists
  get hasDevApiTarget() {
    return this.devApiTargets && this.devApiTargets.length > 0
  }

  highlightJson(json) {
    // Replace special characters for HTML
    json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    
    // Add syntax highlighting
    return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, (match) => {
      let cls = 'text-gray-800 dark:text-gray-200' // default
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'text-blue-600 dark:text-blue-400' // key
        } else {
          cls = 'text-green-600 dark:text-green-400' // string value
        }
      } else if (/true|false/.test(match)) {
        cls = 'text-purple-600 dark:text-purple-400' // boolean
      } else if (/null/.test(match)) {
        cls = 'text-red-600 dark:text-red-400' // null
      } else {
        cls = 'text-orange-600 dark:text-orange-400' // number
      }
      return `<span class="${cls}">${match}</span>`
    })
  }

  initTooltips() {
    this.element.querySelectorAll('[data-tooltip]').forEach(el => {
      let tooltip
      el.addEventListener('mouseenter', () => {
        const text = el.getAttribute('data-tooltip')
        tooltip = document.createElement('div')
        tooltip.textContent = text
        tooltip.style.position = 'absolute'
        tooltip.style.backgroundColor = '#333'
        tooltip.style.color = '#fff'
        tooltip.style.padding = '4px 8px'
        tooltip.style.borderRadius = '4px'
        tooltip.style.fontSize = '12px'
        tooltip.style.pointerEvents = 'none'
        tooltip.style.zIndex = '9999'
        document.body.appendChild(tooltip)
      })
      el.addEventListener('mousemove', (e) => {
        if (tooltip) {
          tooltip.style.top = e.pageY + 10 + 'px'
          tooltip.style.left = e.pageX + 10 + 'px'
        }
      })
      el.addEventListener('mouseleave', () => {
        if (tooltip) {
          document.body.removeChild(tooltip)
          tooltip = null
        }
      })
    })
  }
}
